import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { ArrowRight, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  finalizeTransaction,
  useCancelUserSubscription,
  useSetUserMaxSpend,
  useStartUserCheckout,
  useTeamUpgrade,
  useUserBilling,
  useUserBillingPortal,
} from "@/queries/billing";
import { useUser } from "@/queries/user";
import { DodoPayments } from "dodopayments-checkout";
import { openCheckout } from "@/utils/dodo";
import { getErrorMessage } from "@/utils/errors";

const defaultBillingData = {
  estimated_total_cents: 0,
  plan: "free",
  line_items: [],
  dodo_subscription_id: null,
  dodo_org_subscription_id: null,
  dodo_customer_id: null,
  tasks_in_cycle: 0,
  read_only: false,
  max_spend_cents: null as number | null,
  cancel_at_period_end: false,
  cancel_effective_at: null as string | null,
  billing_cycle_end: null as string | null,
  free_task_limit: 50,
};

function firstOfNextMonth(): string {
  const d = new Date();
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return next.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function FreeUsage({
  tasksInCycle,
  limit,
}: {
  tasksInCycle: number;
  limit: number;
}) {
  const used = Math.max(0, tasksInCycle);
  const remaining = Math.max(0, limit - used);
  const pct = Math.min(100, Math.round((used / limit) * 100));
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground">
        {used}/{limit} tasks used this month.
      </p>
      <div className="h-2 w-full overflow-hidden rounded bg-muted">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-muted-foreground">
        {remaining} credit{remaining === 1 ? "" : "s"} remaining. Resets on{" "}
        {firstOfNextMonth()}.
      </p>
    </div>
  );
}

function formatCancelDate(iso: string | null): string {
  if (!iso) return "your next billing date";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "your next billing date"
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

export function UserBillingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useUser();
  const userId = user.data?.user_id ?? "";
  const billing = useUserBilling(userId);
  const startCheckout = useStartUserCheckout(userId);
  const cancel = useCancelUserSubscription(userId);
  const teamUpgrade = useTeamUpgrade(userId);
  const portal = useUserBillingPortal(userId);
  const setMaxSpend = useSetUserMaxSpend(userId);
  const [maxSpendInput, setMaxSpendInput] = useState("");
  const [showTeam, setShowTeam] = useState(false);
  const [orgName, setOrgName] = useState("");

  if (user.isFetching || billing.isLoading) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  }
  const error = user.error || billing.error;
  if (!!error) {
    return <p className="p-6 text-sm text-destructive">{error.message}</p>;
  }
  const data = billing.data ?? defaultBillingData;
  const onUpgradePro = async () => {
    try {
      const res = await startCheckout.mutateAsync();
      await openCheckout(res.checkout_url);
      try {
        DodoPayments.Checkout.close();
      } catch {
        // overlay already closed
      }
      qc.invalidateQueries({ queryKey: ["users", userId, "billing"] });
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const onTeamUpgrade = async () => {
    if (!orgName.trim()) return;
    try {
      const res = await teamUpgrade.mutateAsync({ name: orgName.trim() });
      await openCheckout(res.checkout_url);
      try {
        DodoPayments.Checkout.close();
      } catch {
        // overlay already closed
      }
      await finalizeTransaction(res.checkout_session_id);
      qc.invalidateQueries({ queryKey: ["orgs"] });
      qc.invalidateQueries({ queryKey: ["user"] });
      navigate({ to: "/orgs/$orgId", params: { orgId: res.org_id } });
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <Helmet><title>TodoLLM - Billing</title></Helmet>
      <header className="space-y-2">
        <p className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs uppercase tracking-[0.25em] text-muted-foreground">
          <BadgeDollarSign className="size-3.5" />
          Billing
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Your plan</h1>
        <p className="text-sm text-muted-foreground">
          Current plan: <strong>{data.plan}</strong>
        </p>
      </header>

      {data.read_only && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <p className="font-medium">Billing past due</p>
          <p className="text-destructive/80">
            Your last invoice failed to pay. Task creation is paused until you
            settle the balance via the customer portal below.
          </p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Usage</CardTitle>
          <CardDescription>This billing cycle</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {data.plan === "pro" ? (
            <>
              <p className="text-muted-foreground">
                {data.tasks_in_cycle} task{data.tasks_in_cycle === 1 ? "" : "s"}{" "}
                this cycle. Includes 100 tasks/month, then $0.02/task.
              </p>
              {data.line_items.map((li) => (
                <div key={li.label} className="flex justify-between">
                  <span>
                    {li.label} (× {li.quantity})
                  </span>
                  <span>
                    ${((li.price_cents / 100) * li.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-2 font-semibold">
                <span>Estimated cycle total</span>
                <span>${(data.estimated_total_cents / 100).toFixed(2)}</span>
              </div>
            </>
          ) : (
            <FreeUsage
              tasksInCycle={data.tasks_in_cycle}
              limit={data.free_task_limit}
            />
          )}
        </CardContent>
      </Card>

      {data.plan === "free" && (
        <Card>
          <CardHeader>
            <CardTitle>Upgrade to Pro</CardTitle>
            <CardDescription>
              $2/month includes 100 tasks, then $0.02/task. Unlimited projects.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={onUpgradePro} disabled={startCheckout.isPending}>
              {startCheckout.isPending ? "Opening checkout…" : "Upgrade to Pro"}
              <ArrowRight className="size-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {data.plan === "pro" && (
        <Card>
          <CardHeader>
            <CardTitle>Spending cap</CardTitle>
            <CardDescription>
              Set a maximum monthly invoice. New tasks are blocked when the
              projected cycle cost would exceed the cap. Leave blank for no cap.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Current cap:{" "}
              <strong>
                {data.max_spend_cents == null
                  ? "no cap"
                  : `$${(data.max_spend_cents / 100).toFixed(2)}`}
              </strong>
            </p>
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                const raw = maxSpendInput.trim();
                const cents =
                  raw === "" ? null : Math.round(parseFloat(raw) * 100);
                if (cents != null && (Number.isNaN(cents) || cents < 0)) {
                  toast.error("Enter a non-negative dollar amount");
                  return;
                }
                try {
                  await setMaxSpend.mutateAsync(cents);
                  toast.success(cents == null ? "Cap removed" : "Cap updated");
                  setMaxSpendInput("");
                } catch (err) {
                  toast.error(getErrorMessage(err));
                }
              }}
            >
              <div className="space-y-1">
                <Label htmlFor="max-spend">Cap ($/month)</Label>
                <Input
                  id="max-spend"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder={
                    data.max_spend_cents == null
                      ? "no cap"
                      : (data.max_spend_cents / 100).toFixed(2)
                  }
                  value={maxSpendInput}
                  onChange={(e) => setMaxSpendInput(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={setMaxSpend.isPending}>
                {setMaxSpend.isPending ? "Saving…" : "Save cap"}
              </Button>
              {data.max_spend_cents != null && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={setMaxSpend.isPending}
                  onClick={async () => {
                    try {
                      await setMaxSpend.mutateAsync(null);
                      toast.success("Cap removed");
                      setMaxSpendInput("");
                    } catch (err) {
                      toast.error(getErrorMessage(err));
                    }
                  }}
                >
                  Remove cap
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      {data.plan === "pro" && data.dodo_customer_id && (
        <Card>
          <CardHeader>
            <CardTitle>Manage subscription</CardTitle>
            <CardDescription>
              Update payment method, billing address, or view invoices in the
              customer portal.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              disabled={portal.isPending}
              onClick={async () => {
                try {
                  const res = await portal.mutateAsync();
                  window.open(res.overview, "_blank", "noopener,noreferrer");
                } catch (e) {
                  toast.error(getErrorMessage(e));
                }
              }}
            >
              {portal.isPending ? "Opening…" : "Manage billing"}
            </Button>
            <Button
              variant="outline"
              disabled={portal.isPending}
              onClick={async () => {
                try {
                  const res = await portal.mutateAsync();
                  const url = res.update_payment_method ?? res.overview;
                  window.open(url, "_blank", "noopener,noreferrer");
                } catch (e) {
                  toast.error(getErrorMessage(e));
                }
              }}
            >
              Update payment method
            </Button>
          </CardContent>
        </Card>
      )}

      {data.plan === "pro" && data.dodo_subscription_id && (
        <Card>
          <CardHeader>
            <CardTitle>Cancel Pro subscription</CardTitle>
            <CardDescription>
              {data.cancel_at_period_end
                ? `Pro ends on ${formatCancelDate(
                    data.cancel_effective_at,
                  )}. You keep access until then.`
                : "Cancellation takes effect at the next billing period."}
            </CardDescription>
          </CardHeader>
          {!data.cancel_at_period_end && (
            <CardContent>
              <Button
                variant="outline"
                disabled={cancel.isPending}
                onClick={async () => {
                  try {
                    await cancel.mutateAsync();
                    toast.success(
                      data.billing_cycle_end
                        ? `Pro will cancel on ${formatCancelDate(
                            data.billing_cycle_end,
                          )}`
                        : "Pro cancellation scheduled",
                    );
                  } catch (e) {
                    toast.error(getErrorMessage(e));
                  }
                }}
              >
                {cancel.isPending ? "Cancelling…" : "Cancel Pro"}
              </Button>
            </CardContent>
          )}
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Need a team?</CardTitle>
          <CardDescription>
            Create an organization on the Team plan to invite collaborators.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!showTeam ? (
            <Button variant="outline" onClick={() => setShowTeam(true)}>
              Upgrade to Team
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="team-org-name">Organization name</Label>
                <Input
                  id="team-org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Acme Robotics"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={onTeamUpgrade}
                  disabled={teamUpgrade.isPending || !orgName.trim()}
                >
                  {teamUpgrade.isPending
                    ? "Creating…"
                    : "Create org & checkout"}
                </Button>
                <Button variant="ghost" onClick={() => setShowTeam(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
